<script setup lang="ts">
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { ref } from 'vue'

import type { AccordionProps, AccordionToggleEvent } from './types'

const props = withDefaults(defineProps<AccordionProps>(), {
  multiple: false,
})

const emit = defineEmits<{ toggle: [event: AccordionToggleEvent] }>()
const resetVersions = ref<number[]>([])

function version(index: number): number {
  return resetVersions.value[index] ?? 0
}

function toggle(index: number, id: string, isOpen: boolean): void {
  const nextOpen = !isOpen
  emit('toggle', { id, open: nextOpen })
  if (!nextOpen || props.multiple) return

  resetVersions.value = props.items.map((_, itemIndex) =>
    itemIndex === index ? version(itemIndex) : version(itemIndex) + 1,
  )
}
</script>

<template>
  <div
    class="ui-accordion flex w-full flex-col overflow-hidden rounded-md border border-border bg-surface font-sans text-fg"
  >
    <Disclosure
      v-for="(item, index) in items"
      :key="`${item.id}-${version(index)}`"
      v-slot="{ open }"
      :default-open="item.defaultOpen === true"
      as="div"
      class="ui-accordion__item border-t border-border first:border-t-0"
    >
      <DisclosureButton
        class="ui-accordion__button flex min-h-11 w-full cursor-pointer items-center justify-between gap-sm border-0 bg-transparent px-md py-sm text-start text-[0.8125rem] font-semibold text-inherit outline-none hover:bg-surface-sunken focus-visible:bg-surface-sunken focus-visible:shadow-[inset_0_0_0_2px_var(--ui-color-ring)] disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="item.disabled === true"
        :aria-disabled="item.disabled === true"
        @click="toggle(index, item.id, open)"
      >
        <slot name="label" :item="item" :open="open">{{ item.label }}</slot>
        <span
          class="ui-accordion__icon transition-transform duration-150"
          :class="{ 'is-open rotate-180': open }"
          aria-hidden="true"
        >
          ⌄
        </span>
      </DisclosureButton>
      <DisclosurePanel
        class="ui-accordion__panel px-md pb-md text-[0.8125rem] leading-normal text-muted"
      >
        <slot name="item" :item="item">{{ item.content }}</slot>
      </DisclosurePanel>
    </Disclosure>
  </div>
</template>
